/**
 * AppFooter — React equivalent of the AngularJS fixed-bottom footer navbar.
 *
 * Source: public/index.html lines 88-104, public/layout/footer.js,
 *         public/layout/partials/customize.html, public/layout/partials/hotkeys.html
 *
 * Contains:
 *   1. Fixed-bottom navbar with version number
 *   2. Hotkeys panel (SGStaff only) — slide-up panel with keyboard shortcuts
 *   3. Customize panel (SGStaff only) — theme selector, spinner selector, debug toggles
 *   4. Support link to Village (SGStaff only)
 */

import { useState, useCallback, useEffect } from 'react';
import { useLegacyPermissions } from '@/lib/useLegacyPermissions';
import { useUserSettings, THEMES, SPINNERS, type ThemeDefinition } from '@/hooks/useUserSettings';
import { UiIcon } from '@/ui';

/**
 * Fetch the app version from `/version.txt`.
 *
 * The GitHub Actions build workflow (.github/workflows/build.yml) writes
 * `git describe --tags --abbrev=7 HEAD` into `dist/version.txt` at build
 * time — same file the AngularJS legacy shell reads to render e.g.
 * "v3.111.262-27-gfcc9bb6" in the footer. Fetching at runtime (instead of
 * embedding via Vite env vars) means the footer stays accurate across
 * dev/deploy without a rebuild step.
 *
 * In local Vite dev there is NO `version.txt`, but the SPA fallback returns
 * `index.html` (200 OK). We defensively detect that HTML payload and fall
 * back to "dev" instead of pasting the whole index into the footer.
 */
async function fetchAppVersion(): Promise<string> {
  try {
    const res = await fetch('/version.txt', { cache: 'no-cache' });
    if (!res.ok) return 'dev';
    const contentType = res.headers.get('content-type') ?? '';
    // SPA fallback returns `text/html`. Only accept a plain-text payload
    // — the build step writes text/plain (or the server default for `.txt`).
    if (contentType.includes('html')) return 'dev';
    const text = (await res.text()).trim();
    // Second guard: even if content-type happens to be text/plain, the
    // payload might still be the SPA index (some servers guess wrong).
    // A real version is short and never starts with a tag.
    if (!text || text.startsWith('<') || text.length > 128) return 'dev';
    return text;
  } catch {
    return 'dev';
  }
}

// ── Types ───────────────────────────────────────────────────────────────────

type PanelId = 'hotkeys' | 'customize' | null;
type CustomizeTab = 'Themes' | 'Settings' | 'Spinners';

// ── Hotkeys Panel ───────────────────────────────────────────────────────────

function HotkeysPanel() {
  return (
    <ul className='bg-white border border-angular-border shadow-lg p-3 text-sm w-64'>
      <li className='font-semibold text-center uppercase tracking-wide border-b pb-2 mb-2'>
        Hotkeys
      </li>
      <li className='text-center text-xs font-semibold text-[#555] mb-1'>Global</li>
      <li className='flex justify-between py-0.5'>
        <span>CTRL+ALT+Z</span>
        <span>Workorder History</span>
      </li>
      <li className='py-0.5'>
        <span>CTRL+ALT+H</span>
      </li>
      <li className='flex justify-between py-0.5'>
        <span>CTRL+ALT+A</span>
        <span>Property Texts</span>
      </li>
      <li className='py-0.5'>
        <span>CTRL+ALT+T</span>
      </li>
      <li className='text-center text-xs font-semibold text-[#555] mt-2 mb-1'>Text Panel</li>
      <li className='flex justify-between py-0.5'>
        <span>CTRL+SHIFT+UP</span>
        <span>Prev Text</span>
      </li>
      <li className='flex justify-between py-0.5'>
        <span>CTRL+SHIFT+DOWN</span>
        <span>Next Text</span>
      </li>
      <li className='flex justify-between py-0.5'>
        <span>CTRL+SHIFT+LEFT</span>
        <span>Prev Order</span>
      </li>
      <li className='flex justify-between py-0.5'>
        <span>CTRL+SHIFT+RIGHT</span>
        <span>Next Order</span>
      </li>
    </ul>
  );
}

// ── Customize Panel ─────────────────────────────────────────────────────────

interface CustomizePanelProps {
  isSupport: boolean;
}

function CustomizePanel({ isSupport }: CustomizePanelProps) {
  const {
    settings,
    setTheme,
    setSpinner,
    toggleShowBidIds,
    toggleShowRecordKeys,
    toggleShowDashboardKeys,
  } = useUserSettings();

  const [activeTab, setActiveTab] = useState<CustomizeTab>('Themes');

  const selectedSpinner =
    SPINNERS.find((s) => s.filename === settings.loadingSpinner) ?? SPINNERS[0];

  const handleThemeClick = useCallback(
    (theme: ThemeDefinition) => {
      // Also call AngularJS less.modifyVars if available (embedded mode)
      const lessGlobal = (window as unknown as Record<string, unknown>).less as
        | { modifyVars?: (vars: Record<string, string>) => void }
        | undefined;
      if (lessGlobal?.modifyVars) {
        lessGlobal.modifyVars({
          '@primary': theme.primary,
          '@secondary': theme.secondary,
        });
      }
      setTheme(theme.name);
    },
    [setTheme]
  );

  const handleSpinnerChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const spinner = SPINNERS.find((s) => s.name === e.target.value);
      if (spinner) {
        setSpinner(spinner.filename);
      }
    },
    [setSpinner]
  );

  return (
    <ul className='bg-white border border-angular-border shadow-lg p-3 text-sm w-64'>
      <li className='font-semibold text-center uppercase tracking-wide border-b pb-2 mb-2'>
        Customize
      </li>

      {/* Tab buttons */}
      <li className='flex justify-center mb-3'>
        <div className='inline-flex rounded overflow-hidden border border-angular-blue'>
          {(['Themes', 'Settings', 'Spinners'] as const).map((tab) => (
            <button
              key={tab}
              type='button'
              className={`px-3 py-1 text-xs font-semibold ${
                activeTab === tab
                  ? 'bg-angular-blue text-white'
                  : 'bg-white text-angular-blue hover:bg-angular-table-hover-bg'
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </li>

      {/* Themes tab */}
      {activeTab === 'Themes' &&
        THEMES.map((theme) => (
          <li key={theme.name} className='py-1'>
            <button
              type='button'
              className='flex items-center gap-3 w-full text-left hover:bg-angular-table-hover-bg px-1 py-0.5 rounded'
              onClick={() => handleThemeClick(theme)}
            >
              <span
                className='inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0'
                style={{ background: theme.primary }}
              />
              <span className='capitalize'>{theme.name}</span>
            </button>
          </li>
        ))}

      {/* Settings tab */}
      {activeTab === 'Settings' && isSupport && (
        <>
          <SettingsToggle
            label='Bids - Show Ids'
            checked={Boolean(settings.showBidIds)}
            onChange={toggleShowBidIds}
          />
          <SettingsToggle
            label='Property Record - Show Keys'
            checked={Boolean(settings.showRecordKeys)}
            onChange={toggleShowRecordKeys}
          />
          <SettingsToggle
            label='Work Order Dash - Show Keys'
            checked={Boolean(settings.showDashKeys)}
            onChange={toggleShowDashboardKeys}
          />
        </>
      )}
      {activeTab === 'Settings' && !isSupport && (
        <li className='text-angular-text-muted text-xs text-center py-2'>No settings available</li>
      )}

      {/* Spinners tab */}
      {activeTab === 'Spinners' && (
        <li>
          <select
            className='w-full border border-angular-border rounded px-2 py-1 text-sm mb-3'
            value={selectedSpinner.name}
            onChange={handleSpinnerChange}
          >
            {SPINNERS.map((spinner) => (
              <option key={spinner.filename} value={spinner.name}>
                {spinner.name}
              </option>
            ))}
          </select>
          <div className='text-center'>
            <img
              src={`/Content/images/spinners/${selectedSpinner.filename}`}
              alt={selectedSpinner.name}
              className='inline-block w-1/2'
            />
          </div>
        </li>
      )}
    </ul>
  );
}

// ── Settings Toggle ─────────────────────────────────────────────────────────

interface SettingsToggleProps {
  label: string;
  checked: boolean;
  onChange: () => void;
}

function SettingsToggle({ label, checked, onChange }: SettingsToggleProps) {
  return (
    <li className='flex items-center justify-between py-1 text-xs font-semibold uppercase'>
      <span>{label}</span>
      <input type='checkbox' checked={checked} onChange={onChange} className='ml-2 scale-110' />
    </li>
  );
}

// ── AppFooter ───────────────────────────────────────────────────────────────

export function AppFooter() {
  const legacyPermissions = useLegacyPermissions();
  const isSGStaff = Boolean(legacyPermissions.SGStaff ?? false);
  const isSupport = Boolean(legacyPermissions.isSupport ?? false);

  const [openPanel, setOpenPanel] = useState<PanelId>(null);
  const [appVersion, setAppVersion] = useState<string>('');

  // Load version.txt once on mount — matches the AngularJS FooterController's
  // one-time read of `$.Version` at page load.
  useEffect(() => {
    let cancelled = false;
    fetchAppVersion().then((v) => {
      if (!cancelled) setAppVersion(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePanel = useCallback((panel: PanelId) => {
    setOpenPanel((prev) => (prev === panel ? null : panel));
  }, []);

  return (
    <nav
      className='fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-angular-border'
      role='navigation'
      style={{ height: '25px', fontSize: '10px' }}
    >
      <div className='flex items-center justify-between h-full px-3'>
        {/* Version — populated from `/version.txt` written by the build
            workflow (git describe output). Empty until the fetch resolves. */}
        <div className='pt-[5px] text-[11px]'>{appVersion}</div>

        {/* Right-side items (SGStaff only) */}
        {isSGStaff && (
          <ul
            className='flex items-center list-none m-0 p-0 gap-6'
            style={{ marginRight: '100px' }}
          >
            {/* Hotkeys — AngularJS: <i class="fa fa-keyboard-o fa-2x"></i> + "Hotkeys"
                from public/SPIGlassApp/partials/slidePanel/slidePanel.html. */}
            <li className='relative'>
              <button
                type='button'
                className='footer-action-btn'
                onClick={() => togglePanel('hotkeys')}
              >
                <UiIcon name='keyboard-o' className='text-lg mr-1' />
                Hotkeys
              </button>
              {openPanel === 'hotkeys' && (
                <div className='absolute bottom-full right-0 mb-1'>
                  <HotkeysPanel />
                </div>
              )}
            </li>

            {/* Customize — AngularJS: <i class="fa fa-sliders fa-2x"></i> + "Customize" */}
            <li className='relative'>
              <button
                type='button'
                className='footer-action-btn'
                onClick={() => togglePanel('customize')}
              >
                <UiIcon name='sliders' className='text-lg mr-1' />
                Customize
              </button>
              {openPanel === 'customize' && (
                <div className='absolute bottom-full right-0 mb-1'>
                  <CustomizePanel isSupport={isSupport} />
                </div>
              )}
            </li>

            {/* Support */}
            <li>
              <a
                href='https://village.safeguardproperties.com/spiglass/pages/spiglass-resources.aspx'
                target='_blank'
                rel='noopener noreferrer'
                className='footer-action-btn'
              >
                <UiIcon name='question-circle-o' className='text-lg mr-1' />
                Support
              </a>
            </li>
          </ul>
        )}
      </div>
    </nav>
  );
}
